import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Heart,
  ChatCircle,
  UserPlus,
  At,
  Broadcast,
  SealCheck,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import RightRail from "@/components/RightRail";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWs, useWsEvent } from "@/context/WebSocketContext";

const iconByType = {
  like: Heart,
  comment: ChatCircle,
  follow: UserPlus,
  mention: At,
  event: Broadcast,
  message: PaperPlaneTilt,
};

const colorByType = {
  like: "text-[#FF0033]",
  comment: "text-[#FF3E00]",
  follow: "text-[#00FF66]",
  mention: "text-[#0033FF]",
  event: "text-[#FF3E00]",
  message: "text-[#FF3E00]",
};

function relativeTime(iso) {
  if (!iso) return "";
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  } catch {
    return "";
  }
}

export default function Notifications() {
  const { user } = useAuth();
  const { setUnreadNotifs } = useWs();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user || typeof user !== "object") {
      setLoading(false);
      return;
    }
    load();
  }, [user, load]);

  // On page open, mark everything as read
  useEffect(() => {
    if (!user || typeof user !== "object") return;
    api.post("/notifications/read").catch(() => {});
    setUnreadNotifs(0);
  }, [user, setUnreadNotifs]);

  // Live: prepend new notifications as they arrive
  const onNotif = useCallback((data) => {
    if (!data) return;
    setItems((prev) => {
      if (prev.some((n) => n.id === data.id)) return prev;
      return [data, ...prev];
    });
    // auto-mark as read since the user is looking at the page
    api.post(`/notifications/${data.id}/read`).catch(() => {});
  }, []);
  useWsEvent("notification", onNotif);

  if (!user || typeof user !== "object") {
    return (
      <Layout right={<RightRail />}>
        <div className="p-10">
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em]">
            // ACTIVIDAD
          </div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-2">
            Ingresa para ver tus notificaciones.
          </h1>
          <Link
            to="/ingresar"
            className="inline-block mt-6 btn-brutal bg-[#FF3E00] text-white font-mono uppercase text-xs tracking-[0.22em] font-bold px-5 py-2"
          >
            → Ingresar
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout right={<RightRail />}>
      <div data-testid="notifications-page">
        <header className="sticky top-0 z-20 bg-[#050505]/90 backdrop-blur-lg border-b border-[#27272A] p-4 md:p-6">
          <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-1">
            ACTIVIDAD EN TIEMPO REAL
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tighter uppercase">
            Notificaciones
          </h1>
        </header>

        {loading ? (
          <div className="p-10 font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">
            cargando…
          </div>
        ) : error ? (
          <div className="p-10 font-mono text-[11px] text-[#FF8899]">{error}</div>
        ) : items.length === 0 ? (
          <div className="p-10">
            <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-2">
              // BANDEJA LIMPIA
            </div>
            <h2 className="font-display font-black text-3xl uppercase tracking-tight">
              Sin actividad por ahora.
            </h2>
            <p className="font-mono text-[11px] text-[#A1A1AA] tracking-wider mt-2">
              Cuando alguien dé like, comente, te siga o te envíe un mensaje, aparecerá aquí.
            </p>
          </div>
        ) : (
          <ul>
            {items.map((n) => {
              const Icon = iconByType[n.type] || Broadcast;
              const color = colorByType[n.type] || "text-[#FF3E00]";
              const isUnread = !n.read;
              const linkTo =
                n.type === "message"
                  ? `/mensajes?with=${n.actor?.handle}`
                  : n.link || `/artista/${n.actor?.handle}`;
              return (
                <li
                  key={n.id}
                  data-testid={`notif-${n.id.slice(0, 8)}`}
                  className={`flex items-start gap-4 p-4 md:p-5 border-b border-[#27272A] hover:bg-[#0a0a0a] transition-colors ${
                    isUnread ? "bg-[#121212]/60" : ""
                  }`}
                >
                  <div
                    className={`h-10 w-10 border border-[#27272A] inline-flex items-center justify-center shrink-0 ${color}`}
                  >
                    <Icon size={18} weight="fill" />
                  </div>
                  <Link to={`/artista/${n.actor?.handle}`} className="shrink-0">
                    <div className="h-10 w-10 bg-[#121212] border border-[#27272A]">
                      {n.actor?.photo ? (
                        <img
                          src={n.actor.photo}
                          alt={n.actor.name}
                          className="h-10 w-10 object-cover"
                        />
                      ) : null}
                    </div>
                  </Link>
                  <Link to={linkTo} className="flex-1 min-w-0 block">
                    <p className="text-[14px] leading-snug">
                      <span className="font-display font-bold hover:text-[#FF3E00] inline-flex items-center gap-1">
                        {n.actor?.name || n.actor?.handle || "Alguien"}
                        {n.actor?.verified ? (
                          <SealCheck size={12} weight="fill" className="text-[#FF3E00]" />
                        ) : null}
                      </span>{" "}
                      <span className="text-[#A1A1AA]">{n.text}</span>
                      {n.target ? (
                        <span className="text-[#F4F4F5]"> · {n.target}</span>
                      ) : null}
                    </p>
                    <div className="font-mono text-[10px] text-[#A1A1AA] tracking-wider mt-1">
                      HACE {relativeTime(n.created_at).toUpperCase()}
                      {isUnread ? (
                        <span className="ml-2 text-[#FF3E00]">● NUEVO</span>
                      ) : null}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}

        <div className="p-10 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-[#A1A1AA]">
          · fin de la actividad ·
        </div>
      </div>
    </Layout>
  );
}
